import { useEffect, useState } from "react";
import axios from "axios";
import { Compass, Users, ClipboardList, CalendarCheck, Wand2, X, MessageCircle, Hash } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, AreaChart, Area, BarChart, Cell,
} from "recharts";

const BASE = "";

function Kpi({ label, value, icon: Icon, hint, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp onClick={onClick}
      className={`bg-white rounded-xl border border-[#E6E1D6] p-4 text-left ${onClick ? "hover:border-[#4A5E2E] hover:shadow-sm cursor-pointer transition" : ""}`}
      data-testid={`coach-kpi-${label}`}>
      <div className="flex items-center gap-2 text-[#978D78] mb-1">
        <Icon size={15} />
        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold">{label}</span>
      </div>
      <div className="text-2xl font-bold text-[#0F1829]">{value}</div>
      {hint && <div className="text-xs text-[#978D78] mt-0.5">{hint}</div>}
    </Comp>
  );
}

function ClientsModal({ mes, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("ihtp_token");
        const url = `${BASE}/api/coach/admin/clients${mes ? `?mes=${mes}` : ""}`;
        const { data } = await axios.get(url,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [mes]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="coach-clients-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F0E7]">
          <div>
            <h3 className="text-lg font-bold text-[#0F1829]">
              Clientes com Roda da Vida {mes ? `— ${mes}` : "(todos)"}
            </h3>
            {data && <p className="text-xs text-[#978D78]">{data.total} cliente(s)</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#F5F2EB] text-[#978D78]" data-testid="coach-clients-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto">
          {err && <p className="p-6 text-rose-500 text-sm">{err}</p>}
          {!data && !err && <p className="p-6 text-slate-400 text-sm">A carregar...</p>}
          {data && (
            <table className="w-full text-sm">
              <thead className="bg-[#F5F2EB] sticky top-0">
                <tr>
                  {["Nome", "Email", "Consultor", "Telefone", "Aval."].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F0E7]">
                {data.clientes.map((c) => (
                  <tr key={c.id_cliente} className="hover:bg-[#FAF8F3]" data-testid={`coach-client-row-${c.id_cliente}`}>
                    <td className="px-4 py-2.5 font-medium text-[#0F1829]">{c.nome}<span className="text-[#978D78] font-normal"> · {c.id_cliente}</span></td>
                    <td className="px-4 py-2.5">{c.email ? <a href={`mailto:${c.email}`} className="text-[#4A5E2E] hover:underline">{c.email}</a> : "—"}</td>
                    <td className="px-4 py-2.5">{c.consultor}</td>
                    <td className="px-4 py-2.5">{c.telefone ? <a href={`tel:${c.telefone}`} className="text-[#4A5E2E] hover:underline">{c.telefone}</a> : "—"}</td>
                    <td className="px-4 py-2.5">{c.avaliacoes}</td>
                  </tr>
                ))}
                {data.clientes.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-[#978D78]">Sem clientes neste período.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function CasaisSection() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("ihtp_token");
        const { data } = await axios.get(`${BASE}/api/coach/admin/casal-metrics`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) return <p className="text-rose-400 text-sm">{err}</p>;
  if (!data) return <p className="text-slate-400 text-sm">A carregar métricas de casais...</p>;
  const k = data.kpis;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-PT") : "—");

  return (
    <div className="space-y-6" data-testid="admin-casais-section">
      <header className="flex items-center gap-3 pt-4 border-t border-[#E6E1D6]">
        <Users size={22} className="text-[#4A5E2E]" />
        <div>
          <h2 className="text-xl font-bold">Avaliação de Casal</h2>
          <p className="text-xs text-slate-500">Casais, análises de compatibilidade e metas.</p>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Casais activos" value={k.casais_ativos} icon={Users} />
        <Kpi label="Convites pendentes" value={k.convites_pendentes} icon={ClipboardList} />
        <Kpi label="Análises geradas" value={k.analises_geradas} icon={Wand2} />
        <Kpi label="Metas criadas" value={k.metas_total} icon={ClipboardList} />
        <Kpi label="Metas concluídas" value={k.metas_concluidas} icon={CalendarCheck}
          hint={k.metas_total ? `${Math.round((k.metas_concluidas / k.metas_total) * 100)}%` : undefined} />
      </section>

      {(data.casais || []).length > 0 && (
        <section className="bg-white rounded-xl border border-[#E6E1D6] p-5 overflow-x-auto" data-testid="admin-casais-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                <th className="py-1.5">Casal</th><th>Estado</th><th>Desde</th>
                <th className="text-center">Análises</th><th>Última análise</th><th className="text-center">Metas</th>
              </tr>
            </thead>
            <tbody>
              {data.casais.map((c) => (
                <tr key={c.id} className="border-t border-[#F3F0E7]" data-testid={`admin-casal-row-${c.id}`}>
                  <td className="py-2 font-medium">{c.nome1} <span className="text-slate-400">+</span> {c.nome2}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.estado === "AT" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {c.estado === "AT" ? "Activo" : "Pendente"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap">{fmt(c.desde)}</td>
                  <td className="text-center">{c.analises}</td>
                  <td className="whitespace-nowrap">{fmt(c.ultima_analise)}</td>
                  <td className="text-center">{c.metas_concluidas}/{c.metas_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}


function MentorSection() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("ihtp_token");
        const { data } = await axios.get(`${BASE}/api/coach/admin/mentor-metrics`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) return <p className="text-rose-400 text-sm">{err}</p>;
  if (!data) return <p className="text-slate-400 text-sm">A carregar métricas do Mentor...</p>;

  const k = data.kpis;
  const maxOcorr = Math.max(1, ...(data.temas || []).map((t) => t.ocorrencias));

  return (
    <div className="space-y-6" data-testid="admin-mentor-section">
      <header className="flex items-center gap-3 pt-4 border-t border-[#E6E1D6]">
        <MessageCircle size={22} className="text-[#4A5E2E]" />
        <div>
          <h2 className="text-xl font-bold">Mentor IHTP — Utilização</h2>
          <p className="text-xs text-slate-500">Perguntas feitas ao Mentor pelos clientes e temas mais procurados.</p>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Perguntas (total)" value={k.total_perguntas} icon={MessageCircle} />
        <Kpi label="Clientes (Mentor)" value={k.total_clientes} icon={Users} hint="clientes únicos" />
        <Kpi label="Conversas" value={k.total_conversas} icon={ClipboardList} />
        <Kpi label="Perguntas este mês" value={k.perguntas_mes_actual} icon={CalendarCheck} />
        <Kpi label="Clientes este mês" value={k.clientes_mes_actual} icon={CalendarCheck} />
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-xl border border-[#E6E1D6] p-5" data-testid="mentor-chart-mensal">
          <h3 className="text-sm font-semibold text-[#0F1829] mb-3">Perguntas por mês</h3>
          <div className="w-full h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.months} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
                <CartesianGrid stroke="#F3F0E7" />
                <XAxis dataKey="mes" tick={{ fill: "#978D78", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#978D78", fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E6E1D6", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="perguntas" name="Perguntas" fill="#4A5E2E" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="clientes" name="Clientes" stroke="#0F1829" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {(data.months || []).length === 0 && (
            <p className="text-xs text-[#978D78] text-center -mt-6">Ainda sem perguntas registadas.</p>
          )}
        </section>

        <section className="bg-white rounded-xl border border-[#E6E1D6] p-5" data-testid="mentor-temas">
          <div className="flex items-center gap-2 mb-1">
            <Hash size={15} className="text-[#4A5E2E]" />
            <h3 className="text-sm font-semibold text-[#0F1829]">Temas mais procurados</h3>
          </div>
          <p className="text-xs text-[#978D78] mb-4">Termos mais frequentes nas perguntas dos clientes ao Mentor.</p>
          {(data.temas || []).length === 0 && (
            <p className="text-sm text-[#978D78]">Ainda não há perguntas suficientes para identificar temas.</p>
          )}
          <ul className="space-y-2">
            {(data.temas || []).map((t, i) => (
              <li key={t.tema} className="flex items-center gap-3" data-testid={`mentor-tema-${i}`}>
                <span className="w-5 text-[11px] text-[#978D78] text-right">{i + 1}.</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-[#0F1829] capitalize">{t.tema}</span>
                    <span className="text-xs text-[#978D78]">{t.ocorrencias}×</span>
                  </div>
                  <div className="h-1.5 bg-[#F3F0E7] rounded-full mt-1">
                    <div className="h-1.5 rounded-full bg-[#4A5E2E]"
                      style={{ width: `${Math.max(6, (t.ocorrencias / maxOcorr) * 100)}%` }} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

export default function AdminCoachVirtual() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [clientsMes, setClientsMes] = useState(undefined); // undefined=fechado | null=todos | "YYYY-MM"

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("ihtp_token");
        const { data } = await axios.get(`${BASE}/api/coach/admin/metrics`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="text-slate-400">A carregar métricas...</p>;
  if (err) return <p className="text-rose-400">{err}</p>;
  if (!data) return null;

  const k = data.kpis;
  const months = data.months.map((m) => ({ ...m, label: m.mes }));

  return (
    <div className="space-y-6 p-1" data-testid="admin-coach-virtual">
      <header className="flex items-center gap-3">
        <Compass size={22} className="text-[#4A5E2E]" />
        <div>
          <h1 className="text-2xl font-bold">Coach Virtual — Adesão</h1>
          <p className="text-xs text-slate-500">Clientes que preencheram a avaliação «Roda da Vida», por mês e acumulado.</p>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Clientes (total)" value={k.total_clientes} icon={Users} hint="clientes únicos · clicar p/ ver" onClick={() => setClientsMes(null)} />
        <Kpi label="Avaliações (total)" value={k.total_avaliacoes} icon={ClipboardList} />
        <Kpi label="Clientes este mês" value={k.clientes_mes_actual} icon={CalendarCheck} hint="clicar p/ ver" onClick={() => setClientsMes(new Date().toISOString().slice(0, 7))} />
        <Kpi label="Avaliações este mês" value={k.avaliacoes_mes_actual} icon={CalendarCheck} />
        <Kpi label="Com insights IA" value={k.com_insights} icon={Wand2} hint="avaliações analisadas" />
      </section>

      <section className="bg-white rounded-xl border border-[#E6E1D6] p-5" data-testid="coach-chart-mensal">
        <h2 className="text-sm font-semibold text-[#0F1829] mb-3">Por mês — clientes e avaliações</h2>
        <div className="w-full h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={months} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
              <CartesianGrid stroke="#F3F0E7" />
              <XAxis dataKey="label" tick={{ fill: "#978D78", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#978D78", fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E6E1D6", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="clientes" name="Clientes no mês" fill="#4A5E2E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="novos_clientes" name="Novos clientes" fill="#978D78" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="avaliacoes" name="Avaliações" stroke="#0F1829" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-[#E6E1D6] p-5" data-testid="coach-chart-areas">
        <h2 className="text-sm font-semibold text-[#0F1829] mb-1">Média por área — onde os clientes mais precisam de apoio</h2>
        <p className="text-xs text-[#978D78] mb-3">
          Média da pontuação (0–10) na avaliação mais recente de cada cliente. As áreas mais fracas aparecem primeiro.
        </p>
        <div className="w-full" style={{ height: Math.max(240, (data.areas?.length || 0) * 42) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.areas} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 60 }}>
              <CartesianGrid stroke="#F3F0E7" horizontal={false} />
              <XAxis type="number" domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fill: "#978D78", fontSize: 11 }} />
              <YAxis type="category" dataKey="area" width={140} tick={{ fill: "#0F1829", fontSize: 12 }} />
              <Tooltip
                formatter={(v, name, p) => [`${v}/10 (min ${p.payload.minimo} · max ${p.payload.maximo} · ${p.payload.clientes} clientes)`, "Média"]}
                contentStyle={{ borderRadius: 8, border: "1px solid #E6E1D6", fontSize: 12 }}
              />
              <Bar dataKey="media" name="Média" radius={[0, 4, 4, 0]}
                label={{ position: "right", fill: "#0F1829", fontSize: 12, formatter: (v) => v.toFixed(1) }}>
                {(data.areas || []).map((a) => (
                  <Cell key={a.area} fill={a.media < 5 ? "#B91C1C" : a.media < 7 ? "#C2831A" : "#4A5E2E"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-4 mt-2 text-[11px] text-[#978D78]">
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1" style={{ background: "#B91C1C" }} />&lt; 5 — precisa de apoio urgente</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1" style={{ background: "#C2831A" }} />5–7 — a melhorar</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1" style={{ background: "#4A5E2E" }} />≥ 7 — saudável</span>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-[#E6E1D6] p-5" data-testid="coach-chart-acumulado">
        <h2 className="text-sm font-semibold text-[#0F1829] mb-3">Acumulado</h2>
        <div className="w-full h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={months} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
              <defs>
                <linearGradient id="gCli" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4A5E2E" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#4A5E2E" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gAval" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F1829" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#0F1829" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#F3F0E7" />
              <XAxis dataKey="label" tick={{ fill: "#978D78", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#978D78", fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E6E1D6", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="clientes_acumulados" name="Clientes acumulados" stroke="#4A5E2E" strokeWidth={2} fill="url(#gCli)" />
              <Area type="monotone" dataKey="avaliacoes_acumuladas" name="Avaliações acumuladas" stroke="#0F1829" strokeWidth={2} fill="url(#gAval)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-[#E6E1D6] overflow-x-auto" data-testid="coach-table-mensal">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#F5F2EB]">
            <tr>
              {["Mês", "Clientes", "Novos", "Avaliações", "Clientes acum.", "Avaliações acum."].map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F0E7]">
            {[...months].reverse().map((m) => (
              <tr key={m.mes} className="hover:bg-[#FAF8F3]">
                <td className="px-4 py-2 font-medium text-[#0F1829]">{m.mes}</td>
                <td className="px-4 py-2">
                  <button onClick={() => setClientsMes(m.mes)} data-testid={`coach-mes-clientes-${m.mes}`}
                    className="text-[#4A5E2E] font-medium hover:underline">{m.clientes}</button>
                </td>
                <td className="px-4 py-2">{m.novos_clientes}</td>
                <td className="px-4 py-2">{m.avaliacoes}</td>
                <td className="px-4 py-2">{m.clientes_acumulados}</td>
                <td className="px-4 py-2">{m.avaliacoes_acumuladas}</td>
              </tr>
            ))}
            {months.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#978D78]">Ainda sem avaliações registadas.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <MentorSection />

      <CasaisSection />

      {clientsMes !== undefined && (
        <ClientsModal mes={clientsMes} onClose={() => setClientsMes(undefined)} />
      )}
    </div>
  );
}

